/**
 * XboxAPIClient module for interfacing with Xbox Live data via OpenXBL (xbl.io)
 * ES Module version for web UI - uses backend proxy to avoid CORS and hide API keys
 */

import axios from "axios";

const BACKEND_PROXY = (
  import.meta.env.VITE_PROXY_URL || "http://localhost:4005"
).replace(/\/+$/, "");

/**
 * Fetches title history (games played) for an Xbox user by gamertag
 * @param {string} gamertag - Xbox gamertag
 * @param {string} [user='benny'] - Which user's API key to use
 * @returns {Promise<Object>} - { success, data: { games, xuid } } or error
 */
/**
 * Fetches MinutesPlayed stat for a specific Xbox title
 * @param {string} titleId - Xbox title ID
 * @param {string} [user='benny'] - Which user's API key to use
 * @returns {Promise<Object>} - { success, minutesPlayed } or error
 */
export async function getXboxGameStats(titleId, user = 'benny') {
  try {
    const response = await axios.get(`${BACKEND_PROXY}/api/xbox/game-stats/${encodeURIComponent(titleId)}`, {
      params: { user },
      timeout: 15000,
    });

    return {
      success: true,
      minutesPlayed: response.data?.minutesPlayed ?? null,
      allStats: response.data?.allStats || [],
    };
  } catch (error) {
    if (error.request && !error.response) {
      return { success: false, error: "Backend proxy is unavailable" };
    }
    const msg = error.response?.data?.error || error.message;
    return { success: false, error: `Xbox stats error: ${msg}` };
  }
}

/**
 * Fetches title history (games played) for an Xbox user by gamertag
 * @param {string} gamertag - Xbox gamertag
 * @param {string} [user='benny'] - Which user's API key to use
 * @returns {Promise<Object>} - { success, data: { games, xuid } } or error
 */
export async function getXboxTitleHistory(gamertag, user = 'benny') {
  try {
    const response = await axios.get(`${BACKEND_PROXY}/api/xbox/title-history/${encodeURIComponent(gamertag)}`, {
      params: { user },
      timeout: 30000,
    });

    const titles = response.data?.titles || [];
    const xuid = response.data?.xuid || null;

    if (titles.length === 0) {
      return {
        success: false,
        error: `No game history found for "${gamertag}". Check your Xbox privacy settings — "Game history" must be set to "Everyone" at account.xbox.com/settings for the API to access it.`,
      };
    }

    return {
      success: true,
      data: {
        xuid,
        games: titles.map((t) => ({
          titleId: t.titleId?.toString(),
          name: t.name || 'Unknown',
          lastPlayed: t.lastPlayed || t.titleHistory?.lastTimePlayed || null,
          minutesPlayed: t.minutesPlayed || 0,
          earnedAchievements: t.achievement?.currentAchievements || t.earnedAchievements || 0,
          totalAchievements: t.achievement?.totalAchievements || t.totalAchievements || 0,
          currentGamerscore: t.achievement?.currentGamerscore || t.currentGamerscore || 0,
          maxGamerscore: t.achievement?.totalGamerscore || t.maxGamerscore || 0,
          imageUrl: t.displayImage || t.images?.[0]?.url || null,
          devices: t.devices || t.platforms || [],
          _raw: t,
        })),
        gameCount: titles.length,
      },
    };
  } catch (error) {
    if (error.request && !error.response) {
      return { success: false, error: "Backend proxy is unavailable" };
    }
    const msg = error.response?.data?.error || error.message;
    return { success: false, error: `Xbox API error: ${msg}` };
  }
}
